import React, { useState } from 'react';
import { CheckCircleIcon, XCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

export default function UserVerificationModal({ user, onClose, onApprove, onReject, onRequestInfo }) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [additionalInfoRequest, setAdditionalInfoRequest] = useState('');
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [showInfoRequestForm, setShowInfoRequestForm] = useState(false);
  const [verificationChecklist, setVerificationChecklist] = useState({
    personalInfo: {
      emailValid: false,
      dobValid: false,
      nationalityValid: false,
      addressComplete: false,
      maritalStatusValid: false
    },
    contactDetails: {
      phoneValid: false,
      emergencyContactValid: false
    },
    documents: {
      idCardPresent: false,
      idCardClear: false,
      idCardValid: false
    }
  });

  const handleChecklistChange = (section, field) => {
    setVerificationChecklist(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: !prev[section][field]
      }
    }));
  };

  const isVerificationComplete = () => {
    return Object.values(verificationChecklist).every(section =>
      Object.values(section).every(value => value)
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to format address
  const formatAddress = (address) => {
    if (!address) return 'No address provided';
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city || address.county || address.state) {
      const cityCounty = [address.city, address.county || address.state].filter(Boolean).join(', ');
      if (cityCounty) parts.push(cityCounty);
    }
    if (address.postCode || address.postalCode) parts.push(address.postCode || address.postalCode);
    if (address.country) parts.push(address.country);
    return parts.join('\n') || 'No address provided';
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-2xl font-bold text-gray-900">User Verification</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XCircleIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - User Information */}
          <div className="space-y-6">
            {/* Personal Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Full Name</span>
                  <span className="font-medium">
                    {user.personalInfo?.firstName && user.personalInfo?.lastName 
                      ? `${user.personalInfo.firstName} ${user.personalInfo.lastName}`
                      : 'Not provided'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date of Birth</span>
                  <span className="font-medium">
                    {formatDate(user.personalInfo?.dateOfBirth)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Nationality</span>
                  <span className="font-medium">
                    {user.personalInfo?.nationality || 'Not provided'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Marital Status</span>
                  <span className="font-medium">
                    {user.personalInfo?.maritalStatus || 'Not provided'}
                  </span>
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Address</h3>
              <div className="whitespace-pre-line">
                {formatAddress(user.personalInfo?.address)}
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Email</span>
                  <span className="font-medium">{user.email || 'Not provided'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone</span>
                  <span className="font-medium">
                    {user.contactDetails?.phoneNumber || 'Not provided'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Alternative Email</span>
                  <span className="font-medium">
                    {user.contactDetails?.alternativeEmail || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name</span>
                  <span className="font-medium">
                    {user.contactDetails?.emergencyContact?.name || 'Not provided'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Relationship</span>
                  <span className="font-medium">
                    {user.contactDetails?.emergencyContact?.relationship || 'Not provided'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone</span>
                  <span className="font-medium">
                    {user.contactDetails?.emergencyContact?.phone || 'Not provided'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Verification */}
          <div className="space-y-6">
            {/* Document Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Identification Document</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Document Type</span>
                  <span className="font-medium">
                    {user.documents?.idCardType || 'Not provided'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Document Number</span>
                  <span className="font-medium">
                    {user.documents?.idCardNumber || 'Not provided'}
                  </span>
                </div>
              </div>
            </div>

            {/* Verification Checklist */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Verification Checklist</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Personal Information</h4>
                  <div className="space-y-2">
                    {Object.entries(verificationChecklist.personalInfo).map(([key, value]) => (
                      <label key={key} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={() => handleChecklistChange('personalInfo', key)}
                          className="h-4 w-4 text-[#123524] focus:ring-[#123524]/20 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-600">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Contact Details</h4>
                  <div className="space-y-2">
                    {Object.entries(verificationChecklist.contactDetails).map(([key, value]) => (
                      <label key={key} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={() => handleChecklistChange('contactDetails', key)}
                          className="h-4 w-4 text-[#123524] focus:ring-[#123524]/20 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-600">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Documents</h4>
                  <div className="space-y-2">
                    {Object.entries(verificationChecklist.documents).map(([key, value]) => (
                      <label key={key} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={() => handleChecklistChange('documents', key)}
                          className="h-4 w-4 text-[#123524] focus:ring-[#123524]/20 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-600">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col space-y-3">
              <button
                onClick={() => onApprove(user.uid)}
                disabled={!isVerificationComplete()}
                className={`w-full py-2 px-4 rounded-lg text-white font-medium
                  ${isVerificationComplete()
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-gray-300 cursor-not-allowed'
                  }`}
              >
                Approve Registration
              </button>
              
              <button
                onClick={() => setShowRejectionForm(true)}
                className="w-full py-2 px-4 rounded-lg text-white font-medium bg-red-600 hover:bg-red-700"
              >
                Reject Registration
              </button>
              
              <button
                onClick={() => setShowInfoRequestForm(true)}
                className="w-full py-2 px-4 rounded-lg text-[#123524] font-medium border border-[#123524] hover:bg-[#123524]/5"
              >
                Request Additional Information
              </button>
            </div>

            {/* Rejection Form */}
            {showRejectionForm && (
              <div className="mt-4 p-4 bg-red-50 rounded-lg">
                <h4 className="font-medium text-red-900 mb-2">Rejection Reason</h4>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full p-2 border border-red-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                  rows="3"
                  placeholder="Enter reason for rejection..."
                />
                <div className="mt-3 flex justify-end space-x-2">
                  <button
                    onClick={() => setShowRejectionForm(false)}
                    className="px-3 py-1.5 text-red-700 hover:bg-red-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      onReject(user.uid, rejectionReason);
                      setShowRejectionForm(false);
                    }}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Confirm Rejection
                  </button>
                </div>
              </div>
            )}

            {/* Additional Information Request Form */}
            {showInfoRequestForm && (
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                <h4 className="font-medium text-yellow-900 mb-2">Request Additional Information</h4>
                <textarea
                  value={additionalInfoRequest}
                  onChange={(e) => setAdditionalInfoRequest(e.target.value)}
                  className="w-full p-2 border border-yellow-300 rounded-lg focus:ring-yellow-500 focus:border-yellow-500"
                  rows="3"
                  placeholder="Specify what additional information is needed..."
                />
                <div className="mt-3 flex justify-end space-x-2">
                  <button
                    onClick={() => setShowInfoRequestForm(false)}
                    className="px-3 py-1.5 text-yellow-700 hover:bg-yellow-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      onRequestInfo(user.uid, additionalInfoRequest);
                      setShowInfoRequestForm(false);
                    }}
                    className="px-3 py-1.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                  >
                    Send Request
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 